import { Card, CardColor, GamePhase, GameState, Player, Tower } from '../types'
import { DECK_COMPOSITION } from '../cardCatalog'
import { xmur3, mulberry32 } from './random'

// Logging disabled to improve FPS
let detailedLogging = false

const log = (tag: string, message: string, data?: any) => {
  if (detailedLogging) {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 12)
    if (data !== undefined) {
      console.groupCollapsed(`[${timestamp}] [${tag}] ${message}`)
      console.log(JSON.parse(JSON.stringify(data)))
      console.groupEnd()
    } else {
      console.log(`[${timestamp}] [${tag}] ${message}`)
    }
  }
}

const shuffle = <T,>(array: T[], seed: string): T[] => {
  const newArray = [...array]
  const seedGen = xmur3(seed)
  const rand = mulberry32(seedGen())
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
      ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

export const generateTowerId = (color: CardColor, existingTowers: Tower[]): string => {
  const sameColorTowers = existingTowers.filter(t => t.color === color)
  if (sameColorTowers.length === 0) {
    return color
  }
  let maxNum = 0
  sameColorTowers.forEach(t => {
    if (t.id === color) {
      maxNum = Math.max(maxNum, 0)
    }
    const parts = t.id.split('_')
    if (parts.length > 1) {
      const num = parseInt(parts[1], 10)
      if (!isNaN(num)) {
        maxNum = Math.max(maxNum, num)
      }
    }
  })
  return `${color}_${maxNum + 1}`
}

export const startTowerPurchase = (state: GameState): GameState => {
  log('startTowerPurchase', 'Player initiated tower purchase.')
  return { ...state, phase: GamePhase.TowerPurchase, discardMode: {} }
}

export const cancelTowerPurchase = (state: GameState): GameState => {
  log('cancelTowerPurchase', 'Player canceled tower purchase.')
  return { ...state, phase: GamePhase.Combat }
}

export const purchaseTower = (state: GameState, playerId: number, towerColor: CardColor, preferOpponent?: boolean): GameState => {
  log('purchaseTower', `Player ${playerId} attempts to purchase ${towerColor} tower.`)
  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1) return state
  let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(state.players))
  let player = playersCopy[playerIndex]
  if (player.capturedTowerThisTurn) {
    log('purchaseTower', 'Purchase failed: Already captured a tower this turn.')
    return { ...state, log: [...state.log, `t:log.alreadyCaptured|playerName=${player.name}`] }
  }
  const opponentIndex = playerIndex === 0 ? 1 : 0
  let opponent = playersCopy[opponentIndex]
  let newAvailableTowers = [...state.availableTowers]
  const newLog = [...state.log]
  const opponentHasTower = opponent.towers.some(t => t.color === towerColor)
  const isNeutralTower = newAvailableTowers.includes(towerColor)
  const isOpponentTower = opponentHasTower && preferOpponent !== false
  const cost = isOpponentTower ? 4 : 3
  log('purchaseTower', `Tower type: ${isOpponentTower ? 'Opponent' : 'Neutral'}. Cost: ${cost}.`)
  if (!isOpponentTower && !isNeutralTower) {
    log('purchaseTower', 'Purchase failed: Tower not available.')
    return { ...state, log: [...state.log, 't:log.towerNotAvailable'] }
  }
  const paymentCards = player.hand.filter(c => c.color === towerColor && !c.essenceCost)
  if (paymentCards.length < cost) {
    log('purchaseTower', `Purchase failed: Not enough cards. Has ${paymentCards.length}, needs ${cost}.`)
    return { ...state, log: [...state.log, `t:log.notEnoughCards|playerName=${player.name}|towerColor=${towerColor}`] }
  }
  const cardIdsToPay = paymentCards.slice(0, cost).map(c => c.id)
  const cardsPaid = player.hand.filter(c => cardIdsToPay.includes(c.id))
  player.hand = player.hand.filter(c => !cardIdsToPay.includes(c.id))
  const newColorDecks = { ...state.colorDecks }
  newColorDecks[towerColor] = shuffle([...newColorDecks[towerColor], ...cardsPaid], state.seed + '_tower_purchase_pay_' + state.log.length)
  if (player.hero === 'ASTRID') {
    const current = player.astridCharges?.[towerColor] || 0
    player.astridCharges = { ...(player.astridCharges || {}), [towerColor]: current + cost }
  }
  if (isOpponentTower) {
    const towerIndex = opponent.towers.findIndex(t => t.color === towerColor)!
    const [stolenTower] = opponent.towers.splice(towerIndex, 1)
    player.towers.push(stolenTower)
    newLog.push(`t:log.towerStole|playerName=${player.name}|targetColor=${towerColor}|defenderName=${opponent.name}`)
    newLog.push(`[REWARD] TOWER_CAPTURE:TYPE=OPPONENT:PLAYER=${playerId}`)

    // Clear towerChangeMenu if it was open for the opponent, as indices shifted
    if (state.towerChangeMenu && state.towerChangeMenu.playerId === opponent.id) {
      state = { ...state, towerChangeMenu: undefined };
    }
  } else {
    newAvailableTowers = newAvailableTowers.filter(t => t !== towerColor)
    const allTowers = [...player.towers, ...opponent.towers]
    const newTowerId = generateTowerId(towerColor, allTowers)
    player.towers.push({ color: towerColor, id: newTowerId })
    newLog.push(`t:log.towerClaimed|playerName=${player.name}|targetColor=${towerColor}`)
    newLog.push(`[REWARD] TOWER_CAPTURE:TYPE=NEUTRAL:PLAYER=${playerId}`)
  }
  player.capturedTowerThisTurn = true
  if (player.towers.length >= 5) {
    log('purchaseTower', `${player.name} has 5 towers. Game over.`)
    return {
      ...state,
      players: playersCopy,
      phase: GamePhase.GameOver,
      winner: player,
      log: [...newLog, `t:log.victorious|playerName=${player.name}`],
      availableTowers: newAvailableTowers,
      colorDecks: newColorDecks,
      pendingVictory: { winnerId: player.id },
    }
  }
  return {
    ...state,
    players: playersCopy,
    log: newLog,
    phase: GamePhase.Combat,
    availableTowers: newAvailableTowers,
    colorDecks: newColorDecks,
  }
}

export const getTowerCost = (state: GameState, towerColor: CardColor): number | null => {
  const opponent = state.players[1]
  const isOpponentTower = opponent.towers.some(t => t.color === towerColor)
  const isNeutralTower = state.availableTowers.includes(towerColor)
  if (!isOpponentTower && !isNeutralTower) return null
  return isOpponentTower ? 4 : 3
}

export const choosePurificationTarget = (state: GameState, targetColor: CardColor): GameState => {
  log('purifyTarget', `Player chose to purify ${targetColor} tower.`)
  let newState = JSON.parse(JSON.stringify(state))
  const initiatorId = newState.purificationInitiatorId
  if (!initiatorId) return state
  const attacker = newState.players.find(p => p.id === initiatorId)!
  const defender = newState.players.find(p => p.id !== initiatorId)!

  // Safety check: should have been caught in activations
  if (attacker.capturedTowerThisTurn) {
    newState.log.push(`t:log.towerFizzle|playerName=${attacker.name}`)
    newState.phase = GamePhase.Combat
    newState.purificationInitiatorId = undefined
    return newState
  }

  let towerVictor: Player | null = null
  let capturedTower = false
  const defenderTowerIndex = defender.towers.findIndex(t => t.color === targetColor)
  if (defenderTowerIndex > -1) {
    const [stolenTower] = defender.towers.splice(defenderTowerIndex, 1)
    attacker.towers.push(stolenTower)
    // Clear towerChangeMenu if it was open for the defender, as indices shifted
    if (newState.towerChangeMenu && newState.towerChangeMenu.playerId === defender.id) {
      newState.towerChangeMenu = undefined
    }
    newState.log.push(`t:log.towerStole|playerName=${attacker.name}|targetColor=${targetColor}|defenderName=${defender.name}|method=purification`)
    capturedTower = true
  } else if (newState.availableTowers.includes(targetColor)) {
    newState.availableTowers = newState.availableTowers.filter(t => t !== targetColor)
    const allTowers = [...newState.players[0].towers, ...newState.players[1].towers]
    attacker.towers.push({ color: targetColor, id: generateTowerId(targetColor, allTowers) })
    newState.log.push(`t:log.towerClaimed|playerName=${attacker.name}|targetColor=${targetColor}|method=purification`)
    capturedTower = true
  } else {
    newState.log.push(`t:log.towerNoTarget|playerName=${attacker.name}`)
  }

  // Set flag if tower was captured
  if (capturedTower) {
    attacker.capturedTowerThisTurn = true
  }

  if (attacker.towers.length >= 5) {
    towerVictor = attacker
    newState.log.push(`t:log.victorious|playerName=${attacker.name}`)
    newState.phase = GamePhase.GameOver
    newState.winner = towerVictor
    newState.pendingVictory = { winnerId: towerVictor.id }
  } else {
    newState.phase = GamePhase.Combat
    newState.turnPlayerId = initiatorId  // Keep turn with the purification initiator
  }
  newState.purificationInitiatorId = undefined
  return newState
}

export const openTowerChangeMenu = (state: GameState, playerId: number, towerIndex: number): GameState => {
  const player = state.players.find(p => p.id === playerId)
  if (!player || !player.towers[towerIndex]) return state
  log('towerChange', `Player ${playerId} opened menu for tower index ${towerIndex}.`)
  return {
    ...state,
    towerChangeMenu: {
      playerId,
      towerIndex,
      currentColor: player.towers[towerIndex].color,
    },
  }
}

export const closeTowerChangeMenu = (state: GameState): GameState => {
  log('towerChange', 'Tower change menu closed.')
  return { ...state, towerChangeMenu: undefined }
}

export const getAvailableColorsForTowerChange = (state: GameState): CardColor[] => {
  if (!state.towerChangeMenu) return []
  const player = state.players.find(p => p.id === state.towerChangeMenu!.playerId)
  if (!player) return []
  const availableColors = new Set<CardColor>()
  player.hand.forEach(card => {
    if (!card.essenceCost) {
      availableColors.add(card.color)
    }
  })
  return Array.from(availableColors)
}

export const changeTowerColor = (state: GameState, newColor: CardColor): GameState => {
  if (!state.towerChangeMenu) return state
  const { playerId, towerIndex } = state.towerChangeMenu
  let newState: GameState = JSON.parse(JSON.stringify(state))
  const player = newState.players.find(p => p.id === playerId)!
  const cardToPayIndex = player.hand.findIndex(c => c.color === newColor && !c.essenceCost)
  if (cardToPayIndex === -1) {
    log('towerChange', 'Failed to change tower: No card to pay with.')
    return { ...newState, towerChangeMenu: undefined }
  }
  const [cardToPay] = player.hand.splice(cardToPayIndex, 1)
  newState.eliminatedCards.push(cardToPay)
  const oldTower = player.towers[towerIndex]
  if (!oldTower) {
    log('towerChange', `Failed to change tower: Tower at index ${towerIndex} not found.`)
    return { ...newState, towerChangeMenu: undefined }
  }
  const oldColor = oldTower.color
  const allExistingTowers = [...newState.players[0].towers, ...newState.players[1].towers]
  const newTowerId = generateTowerId(newColor, allExistingTowers.filter(t => t.id !== oldTower.id))
  player.towers[towerIndex] = { color: newColor, id: newTowerId }
  const originalCardTemplate = DECK_COMPOSITION.find(c => c.color === oldColor)
  if (originalCardTemplate) {
    const returnedCard: Card = {
      ...originalCardTemplate,
      id: `tower-return-${state.seed}-${state.round}-${state.log.length}-${playerId}-${towerIndex}-${oldColor}-${newColor}`
    }
    newState.colorDecks[oldColor].push(returnedCard)
    newState.colorDecks[oldColor] = shuffle(newState.colorDecks[oldColor], newState.seed + `_tchange_return_` + returnedCard.id)
  }
  newState.log.push(`t:log.towerTransform|playerName=${player.name}|oldColor=${oldColor}|newColor=${newColor}`)
  newState.log.push(`[REWARD] TOWER_COLOR_CHANGE:PLAYER=${playerId}`)
  log('towerChange', `${player.name} changed tower from ${oldColor} to ${newColor}.`)
  newState.towerChangeMenu = undefined
  player.changedTowerColorThisTurn = true
  return newState
}
